import { goPage } from '@/utils/browser/page'

import type { JwtDecodeData } from '@/models/interfaces/token.interface'
import { CanceledError } from 'axios'
import { api } from '@/services'
import { useToken } from '@/composables/useToken'
import { useMessage } from '@/composables/useMessage'
import { useUserStore } from '@/stores/user.stores'
import { useSystemVersionStore } from '@/stores/systemVersion.stores'
import { catchErrorHandler } from '@/utils/api/error-handler'
import { getI18nTranslate } from '@/utils/i18nUtils'
import { useContextSwitch } from '@/composables/useContextSwitch'

/** 處理使用者 api 資料邏輯 */
export const useFetchUser = () => {
  const userStore = useUserStore()
  const systemVersionStore = useSystemVersionStore()
  const { getDataFromToken } = useToken()
  const { openMessage } = useMessage()
  const { updateContextSwitchAccount } = useContextSwitch()

  /** 取得使用者資訊 */
  const fnGetUserInfo = async (token: string): Promise<void> => {
    try {
      const { result, isSuccess } = await api.user.getUserInfo()

      if (!isSuccess) {
        // 取使用者資訊失敗視同登入態失效
        userStore.initLoginState()
        return openMessage('error', getI18nTranslate('ErrorMessage.GetUserInfoFail'), {}, () => {
          goPage('/login')
        })
      }

      // userInfo 需合併 API 與 token claim，才是完整權限上下文
      userStore.userInfo.name = result.name
      userStore.userInfo.userId = (getDataFromToken<JwtDecodeData>(token, 'name') as string) || ''
      userStore.userInfo.roleOrder =
        (getDataFromToken<JwtDecodeData>(token, 'roleOrder') as number) || 0
      userStore.userInfo.photoUrl =
        (getDataFromToken<JwtDecodeData>(token, 'photo') as string) || ''
      userStore.userInfo.goodsIsVisibleSwitch =
        (getDataFromToken<JwtDecodeData>(token, 'goodsIsVisibleSwitch') as boolean) || false
      // OP#2459：token 沒帶 timeZoneId 時 default 給 UTC，不再猜測帳號所在時區
      userStore.userInfo.timeZoneId =
        (getDataFromToken<JwtDecodeData>(token, 'timeZoneId') as string) || 'UTC'

      if (userStore.userInfo.userId) {
        updateContextSwitchAccount(userStore.userInfo.userId, userStore.userInfo.name)
      }

      // webVersion/apiVersion 為 2 才代表帳號有授權切換新舊系統/API，Sidebar 的 switch 才會顯示；
      // 未授權的帳號永遠強制為舊系統/舊 API，已授權的帳號則沿用（persist）上次的選擇，不強制覆蓋
      systemVersionStore.isAllowedToSwitchSystem = result.webVersion === 2
      if (!systemVersionStore.isAllowedToSwitchSystem) {
        systemVersionStore.isNewSystem = false
      }

      systemVersionStore.isAllowedToSwitchApi = result.apiVersion === 2
      if (!systemVersionStore.isAllowedToSwitchApi) {
        systemVersionStore.isNewApi = false
      }
    } catch (e) {
      // 主動取消的請求（例如元件 unmount）不代表登入態失效，交給 catchErrorHandler
      // 靜默處理即可
      if (e instanceof CanceledError) {
        catchErrorHandler(e)
        return
      }

      // 取得使用者資訊失敗一律視同登入態失效，不論是業務層失敗（見上方 !isSuccess 分支）
      // 還是 HTTP 層錯誤（例如 OP#2607 後 403 會 reject 而不是 resolve）都要走同一套安全網，
      // 避免帳號被後端停權後，前端仍沿用 persist 的舊 userInfo 繼續運作
      userStore.initLoginState()
      openMessage('error', getI18nTranslate('ErrorMessage.GetUserInfoFail'), {}, () => {
        goPage('/login')
      })
    }
  }

  /** 處理變更使用者資訊 */
  const fnUpdateUserInfo = async (
    params: { userName: string } | { isShowInvisibleGoods: boolean }
  ) => {
    try {
      const { isSuccess } = await api.user.updateUserInfo(params)

      if (!isSuccess) {
        openMessage('error', getI18nTranslate('ErrorMessage.UpdateUserInfoFail'))
        return
      }

      // 更新成功後重拉 userInfo，避免 store 與後端狀態不一致
      fnGetUserInfo(userStore.token)
    } catch (e) {
      catchErrorHandler(e)
    }
  }

  return {
    fnGetUserInfo,
    fnUpdateUserInfo
  }
}
